<!--
  "Listen" — a reconstructed reading of the inscription, with the whole of its uncertainty on
  screen beside it.

  The thing this component exists to prevent is a listener taking what they hear for what is on
  the stone. **The script records no vowels.** Every vowel in the reading was supplied by this
  project, and roughly a quarter of the words are a frequency preference among several equally
  licit readings rather than a reading of anything.

  So the transcript is not an optional extra behind a disclosure — it opens with the control, and
  each word carries its tier. Three dispositions, and they look different at a glance:

  * **spoken** — a vocalization exists. `cited` when it rests on a published grammar, a known
    compound, or a written mater; `convention` when a nominal template fits and so do others.
    A conventional word shows how many rivals it beat, and names them.
  * **spelled** — no vocalization; the letters are read out rather than a word being invented.
  * **illegible** — a lacuna. Never voiced through and never silently skipped, or an incomplete
    text would sound complete.

  Nothing here is stored, exported, or fed to a model. It is a listening aid over a compiled-in
  table, and the epigrapher decides what the text says.
-->
<script lang="ts">
  import type { ReadingPlan } from '../lib/reading';
  import type { TokenOut } from '../lib/api/types';
  import InfoTip from './InfoTip.svelte';

  interface Props {
    tokens: readonly TokenOut[];
    /**
     * The record's normalized siglum key — what the renderer names its files after, and what
     * the corpus itself uses as an identifier. Deliberately NOT the surrogate `inscriptions.id`:
     * that is assigned at load time and is not stable across a reload, so a URL built from it
     * would rot the first time the database was rebuilt.
     */
    siglum?: string | null;
  }

  const { tokens, siglum = null }: Props = $props();

  /** Mirrors `slug()` in phonology/audio/render_readings.mjs, which names the files. */
  function slug(value: string): string {
    return value.replace(/[^A-Za-z0-9._-]/g, (c) => `u${c.codePointAt(0)!.toString(16)}`);
  }

  let open = $state(false);
  let problem = $state<string | null>(null);

  // The word-vocalization table is ~42 KB gzipped and is only ever needed by this panel, so it
  // arrives when the panel does. `#/queue` and `#/browse` never load it.
  let plan = $state<ReadingPlan | null>(null);
  let summary = $state('');
  let grounded = $state<(tier: string) => boolean>(() => false);

  // Whole-inscription audio is rendered offline and served from the imagery bucket, like the
  // photographs. When no base is configured the transcript still works on its own — which is
  // most of the value, and all of the honesty.
  const audioBase = import.meta.env.VITE_AUDIO_BASE as string | undefined;
  const audioUrl = $derived(
    audioBase && siglum ? `${audioBase.replace(/\/$/, '')}/${slug(siglum)}.mp3` : null,
  );

  let element: HTMLAudioElement | null = null;

  async function play(): Promise<void> {
    problem = null;
    if (!audioUrl) {
      problem = 'No audio has been rendered for this deployment; the transcript below is the reading.';
      return;
    }
    try {
      if (!element) element = new Audio();
      element.pause();
      element.src = audioUrl;
      await element.play();
    } catch {
      problem = 'The reading could not be played. The transcript below is the same reading.';
    }
  }

  async function toggle(): Promise<void> {
    open = !open;
    if (!open) return;
    if (!plan) {
      const reading = await import('../lib/reading');
      plan = reading.readingPlan(tokens);
      summary = reading.planSummary(plan);
      grounded = reading.isGrounded;
    }
    void play();
  }

  function tierLabel(tier: string): string {
    if (tier === 'lexicon' || tier === 'theonym') return 'cited';
    if (tier === 'construct') return 'compound';
    if (tier === 'derived') return 'pattern fixed';
    if (tier === 'mater') return 'vowel written';
    return 'convention';
  }
</script>

<div class="listen col">
  <button type="button" class="small trigger" aria-expanded={open} onclick={() => void toggle()}>
    {open ? '▾' : '▸'} Listen
    <span class="muted">— reconstructed</span>
  </button>

  {#if open && plan}
    <div class="panel col" role="note">
      <p class="small warn-text">
        <strong>This is a reconstruction, not a reading.</strong> The script records no vowels; every
        vowel below was supplied by this project. Nothing here is stored or exported.
      </p>
      <p class="small muted">{summary}</p>

      {#if problem}
        <p class="small banner warn">{problem}</p>
      {:else if audioUrl}
        <button type="button" class="small" onclick={() => void play()}>▸ Play again</button>
      {/if}

      <ol class="words">
        {#each plan.utterances as utterance, index (index)}
          <li class="word row" class:spelled={utterance.kind === 'spelled'} class:gap={utterance.kind === 'gap'}>
            <span class="glyph src">{utterance.word}</span>
            <span class="arrow muted">→</span>
            {#if utterance.kind === 'spoken'}
              <span class="form">{utterance.form}</span>
              <span class="badge" class:convention={!grounded(utterance.tier)}>
                {tierLabel(utterance.tier)}{utterance.rivals ? ` · ${utterance.rivals} rivals` : ''}
              </span>
              <!--
                The count is inline; the rival forms themselves were in a title=, which is
                unreachable from the keyboard and gone on touch. They are what tells a reader
                that this reading is one of several, so they get a real note.
              -->
              {#if utterance.rivals > 0}
                <InfoTip label={`the reading of ${utterance.word}`}>
                  <p class="tip-line">
                    <strong>{utterance.rivals} other readings are equally licit:</strong>
                    {utterance.alternatives.join(', ')}.
                  </p>
                  <p class="tip-line muted">
                    Nothing in the inscription chooses between them. This one leads because its
                    pattern is the commoner one, which is a preference, not a reading.
                  </p>
                </InfoTip>
              {/if}
              {#if utterance.gloss}<span class="small muted gloss">{utterance.gloss}</span>{/if}
            {:else if utterance.kind === 'spelled'}
              <span class="form muted">spelled out</span>
              <span class="badge" title="No vocalization is recorded for this word, so its letters are read rather than a word invented">no reading</span>
            {:else}
              <span class="form muted">—</span>
              <span class="badge" title="A lacuna: never voiced through, and never silently skipped">illegible</span>
            {/if}
            {#if utterance.uncertain}<span class="mark" title="Scholar-marked uncertain">?</span>{/if}
            {#if utterance.restored}<span class="mark" title="Scholar-marked restored">[ ]</span>{/if}
          </li>
        {/each}
      </ol>
    </div>
  {/if}
</div>

<style>
  .listen {
    gap: 4px;
  }

  .trigger {
    align-self: flex-start;
    background: none;
    border: none;
    padding: 0;
    color: var(--text-dim);
  }

  .trigger:hover {
    color: var(--text);
  }

  .panel {
    gap: 6px;
    padding: 8px;
    border: 1px solid var(--line);
    border-radius: 6px;
    background: var(--bg-panel);
  }

  .warn-text {
    margin: 0;
  }

  .words {
    list-style: none;
    margin: 0;
    padding: 0;
    display: flex;
    flex-direction: column;
    gap: 3px;
    max-height: 40vh;
    overflow-y: auto;
  }

  .word {
    gap: 6px;
    align-items: baseline;
    flex-wrap: wrap;
  }

  .src {
    min-width: 5ch;
  }

  .arrow {
    font-size: var(--text-xs);
  }

  .form {
    font-weight: 600;
  }

  /* A convention is marked by the word "convention" and this border, never by colour alone. */
  .badge {
    font-size: var(--text-xs);
    padding: 0 4px;
    border: 1px solid var(--line);
    border-radius: 3px;
    color: var(--text-dim);
  }

  .badge.convention {
    border-style: dashed;
    border-color: var(--line-strong);
  }

  .word.gap .form,
  .word.spelled .form {
    font-weight: 400;
  }

  .mark {
    font-size: var(--text-xs);
    color: var(--text-dim);
  }

  .gloss {
    font-style: italic;
  }

  .tip-line {
    margin: 0 0 5px;
  }

  .tip-line:last-child {
    margin-bottom: 0;
  }
</style>
